import ExcelJS from 'exceljs';
import { CareerPeriodType } from '../../../shared/enum/EnumRepository';
import { CAREER_TYPE_VALUES, getCareerPeriodType } from './careerBulkConfig';

export const CAREER_BULK_TEMPLATE_FILE_NAME = 'career_bulk_template.xlsx';
export const CAREER_BULK_TEMPLATE_MAX_ROWS = 1000;
export const CAREER_BULK_TEMPLATE_SHEET_NAME = 'Careers';
export const CAREER_BULK_TEMPLATE_GUIDE_SHEET_NAME = 'Guide';

export const CAREER_BULK_TEMPLATE_HEADERS = [
  'careerType',
  'content',
  'contentEn',
  'startYear',
  'startMonth',
  'startDay',
  'endYear',
  'endMonth',
  'endDay',
  'isCurrent',
  'currentLabelOrig',
  'relatedUrl',
] as const;

const REQUIRED_HEADERS = new Set<string>(['careerType', 'content']);
const HEADER_FILL = 'FF27485D';
const REQUIRED_HEADER_FILL = 'FF173245';
const GUIDE_SECTION_FILL = 'FFDCEAF2';
const BORDER_COLOR = 'FFB9C6CE';

const COLUMN_WIDTHS: Record<(typeof CAREER_BULK_TEMPLATE_HEADERS)[number], number> = {
  careerType: 18,
  content: 42,
  contentEn: 42,
  startYear: 12,
  startMonth: 12,
  startDay: 12,
  endYear: 12,
  endMonth: 12,
  endDay: 12,
  isCurrent: 13,
  currentLabelOrig: 20,
  relatedUrl: 44,
};

const applyHeader = (sheet: ExcelJS.Worksheet): void => {
  const row = sheet.addRow(CAREER_BULK_TEMPLATE_HEADERS);
  row.height = 30;
  row.eachCell({ includeEmpty: true }, (cell) => {
    const required = REQUIRED_HEADERS.has(String(cell.value));
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: required ? REQUIRED_HEADER_FILL : HEADER_FILL },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
    };
  });
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: CAREER_BULK_TEMPLATE_HEADERS.length },
  };
};

const listFormula = (values: string[]): string => `"${values.join(',')}"`;

const applyListValidation = (
  sheet: ExcelJS.Worksheet,
  header: string,
  values: string[],
  allowBlank: boolean,
): void => {
  const columnNumber = CAREER_BULK_TEMPLATE_HEADERS.findIndex(
    (candidate) => candidate === header,
  ) + 1;
  if (columnNumber < 1) return;

  for (let rowNumber = 2; rowNumber <= CAREER_BULK_TEMPLATE_MAX_ROWS + 1; rowNumber += 1) {
    sheet.getCell(rowNumber, columnNumber).dataValidation = {
      type: 'list',
      allowBlank,
      formulae: [listFormula(values)],
      showInputMessage: true,
      promptTitle: header,
      prompt: '목록에서 값을 선택하세요.',
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Invalid value',
      error: '목록에 있는 값만 입력할 수 있습니다.',
    };
  }
};

const createCareersSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(CAREER_BULK_TEMPLATE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  applyHeader(sheet);

  CAREER_BULK_TEMPLATE_HEADERS.forEach((header, index) => {
    const column = sheet.getColumn(index + 1);
    column.width = COLUMN_WIDTHS[header];
    column.numFmt = '@';
    column.alignment = { vertical: 'top', wrapText: true };
  });

  applyListValidation(sheet, 'careerType', CAREER_TYPE_VALUES, false);
  applyListValidation(sheet, 'isCurrent', ['TRUE', 'FALSE'], true);
};

const styleSectionHeader = (sheet: ExcelJS.Worksheet, name: string): void => {
  const row = sheet.addRow([name]);
  sheet.mergeCells(`A${row.number}:C${row.number}`);
  row.getCell(1).font = { bold: true, color: { argb: REQUIRED_HEADER_FILL } };
  row.getCell(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: GUIDE_SECTION_FILL },
  };
};

const styleTableHeader = (row: ExcelJS.Row): void => {
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HEADER_FILL },
    };
  });
};

const createGuideSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(CAREER_BULK_TEMPLATE_GUIDE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  sheet.columns = [{ width: 24 }, { width: 18 }, { width: 90 }];
  sheet.views = [{ state: 'frozen', ySplit: 2 }];

  sheet.mergeCells('A1:C1');
  const title = sheet.getCell('A1');
  title.value = 'Career bulk upload guide';
  title.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  title.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: REQUIRED_HEADER_FILL },
  };
  title.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 34;

  const periodTypes = CAREER_TYPE_VALUES.filter(
    (type) => getCareerPeriodType(type) === CareerPeriodType.PERIOD,
  );
  const pointTypes = CAREER_TYPE_VALUES.filter(
    (type) => getCareerPeriodType(type) === CareerPeriodType.POINT,
  );

  styleSectionHeader(sheet, '작성 방법');
  [
    ['시트/헤더', '', 'Careers 시트명과 1행 헤더를 변경하지 마세요.'],
    ['행 수', '', `데이터는 최대 ${CAREER_BULK_TEMPLATE_MAX_ROWS.toLocaleString()}행입니다.`],
    ['필수값', '', 'careerType과 content는 필수입니다.'],
    ['날짜', '', '연도, 월, 일을 각각 입력합니다. 일을 입력하려면 연도와 월이 필요합니다.'],
    ['현재 진행', '', 'isCurrent=TRUE이면 종료 연월일을 비워야 합니다. 빈 값은 FALSE입니다.'],
    ['URL', '', 'relatedUrl은 http(s) URL 또는 example.com 형식으로 입력합니다.'],
  ].forEach((values) => sheet.addRow(values));

  styleSectionHeader(sheet, 'Career type');
  sheet.addRow(['PERIOD', '', periodTypes.join(', ')]);
  sheet.addRow(['POINT', '', pointTypes.join(', ')]);
  sheet.addRow(['주의', '', 'POINT 타입에는 종료 연월일과 isCurrent=TRUE를 사용할 수 없습니다.']);

  styleSectionHeader(sheet, '필드');
  const fieldHeader = sheet.addRow(['Field', 'Required', 'Description']);
  styleTableHeader(fieldHeader);
  const descriptions: Record<(typeof CAREER_BULK_TEMPLATE_HEADERS)[number], string> = {
    careerType: 'Career 유형 코드. 드롭다운에서 선택',
    content: '경력 내용 (최대 255자)',
    contentEn: '영문 경력 내용 (최대 255자)',
    startYear: '시작 연도 (1~9999)',
    startMonth: '시작 월 (1~12)',
    startDay: '시작 일',
    endYear: '종료 연도. PERIOD 타입만 가능',
    endMonth: '종료 월. PERIOD 타입만 가능',
    endDay: '종료 일. PERIOD 타입만 가능',
    isCurrent: '현재 진행 여부 TRUE/FALSE. 빈 값은 FALSE',
    currentLabelOrig: '현재 상태 표기 (최대 20자, isCurrent=TRUE일 때만)',
    relatedUrl: '관련 URL (최대 500자)',
  };
  CAREER_BULK_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([
      header,
      REQUIRED_HEADERS.has(header) ? 'YES' : '',
      descriptions[header],
    ]);
  });

  styleSectionHeader(sheet, '예시');
  const exampleHeader = sheet.addRow(['Field', 'Value', '']);
  styleTableHeader(exampleHeader);
  const example: Record<(typeof CAREER_BULK_TEMPLATE_HEADERS)[number], string> = {
    careerType: 'EDU',
    content: '홍익대학교 졸업',
    contentEn: 'Graduated from Hongik University',
    startYear: '2020',
    startMonth: '3',
    startDay: '1',
    endYear: '2024',
    endMonth: '2',
    endDay: '29',
    isCurrent: 'FALSE',
    currentLabelOrig: '',
    relatedUrl: 'https://example.com/career',
  };
  CAREER_BULK_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([header, example[header], '']);
  });

  sheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { ...cell.alignment, vertical: 'top', wrapText: true };
    });
  });
};

export const buildCareerBulkTemplateWorkbook = (): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Architrave';
  workbook.title = 'Career bulk upload template';
  workbook.subject = 'Career bulk creation';
  createCareersSheet(workbook);
  createGuideSheet(workbook);
  return workbook;
};

export const downloadCareerBulkTemplate = async (): Promise<void> => {
  const workbook = buildCareerBulkTemplateWorkbook();
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = CAREER_BULK_TEMPLATE_FILE_NAME;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};
